import {TFunction} from "i18next";
import _ from "lodash";
import {getErrorMessage} from "@dreebit-registry/vsm-microservice-components";
import {getEnvironment} from "./getEnvironment";

export const GENERIC_ERROR_MESSAGE = 'Something went wrong.';

/**
 * Localized messages for the structural error codes introduced with IBM-462
 * (system subtree rules). The backend's `message` is English-only tech copy;
 * the machine-readable code lives at `errors[0].extensions.code`.
 */
const STRUCTURAL_ERROR_MESSAGES: { [code: string]: string } = {
    INVALID_PARENT_TYPE: 'This element type cannot be created under this parent.',
    PRESSURE_LINE_LIMIT_EXCEEDED: 'The system has reached its maximum number of pressure lines.',
    ASSET_LIMIT_EXCEEDED: 'The system has reached its maximum number of assets.',
    LAST_PRESSURE_LINE: 'The last pressure line of a connected system cannot be removed.',
    SYSTEM_TYPE_TRANSITION_NOT_ALLOWED: 'The system type of a connected system cannot be changed.',
    SYSTEM_STRUCTURE_EXCEEDS_TARGET_LIMITS: 'The current system structure exceeds the limits of the selected system type.',
};

/**
 * Messages in these shapes are GraphQL spec/runtime errors (validation,
 * coercion, parse failures, JS/PHP leaks) — never copy meant for end users.
 */
const TECHNICAL_MESSAGE_PATTERNS: RegExp[] = [
    /^Variable "\$\w+" got invalid value/,
    /^Variable "\$\w+" of (required )?type /,
    /is not defined by type/,
    /^Unknown (argument|type|directive|fragment) /,
    /^Cannot query field /,
    /^Field "[^"]+" (of type|argument|must)/,
    /^Cannot return null for non-nullable field/,
    /^Expected (type|value of type|Iterable)/,
    /^(String|Int|Float|Boolean|ID) cannot represent /,
    /^Enum "[^"]+" cannot represent /,
    /^Syntax Error/,
    /^Unexpected error value/,
    /^internal server error$/i,
    /^Failed to fetch/,
    /Cannot read propert(y|ies) of/,
    /^Unexpected token .* in JSON/,
];

const TECHNICAL_CODES = new Set([
    'GRAPHQL_PARSE_FAILED',
    'GRAPHQL_VALIDATION_FAILED',
    'BAD_USER_INPUT',
    'BAD_REQUEST',
    'INTERNAL_SERVER_ERROR',
    'OPERATION_RESOLUTION_FAILURE',
    'PERSISTED_QUERY_NOT_FOUND',
    'PERSISTED_QUERY_NOT_SUPPORTED',
]);

// webonyx/graphql-php categories; deliberately without "validation" —
// Lighthouse validation messages are field-level and user-helpful.
const TECHNICAL_CATEGORIES = new Set(['graphql', 'internal']);

// Coercion dumps embed the whole input JSON; legitimate backend copy is far shorter.
const MAX_MESSAGE_LENGTH = 300;

const collectGraphQLErrors = (err: any): any[] => {
    const graphQLErrors = _.get(err, 'graphQLErrors', []) as any[] | undefined;
    const networkErrors = _.get(err, 'networkError.result.errors', []) as any[] | undefined;
    return [...(graphQLErrors ?? []), ...(networkErrors ?? [])];
};

const tryGetErrorMessage = (err: any): string | undefined => {
    // The lib function is not null-safe (throws on missing graphQLErrors/message).
    try {
        return getErrorMessage(err);
    } catch {
        return undefined;
    }
};

const isTechnicalError = (err: any, message: string): boolean => {
    if (message.length > MAX_MESSAGE_LENGTH) {
        return true;
    }
    if (TECHNICAL_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))) {
        return true;
    }
    return collectGraphQLErrors(err).some((gqlError) =>
        TECHNICAL_CODES.has(_.get(gqlError, 'extensions.code')) ||
        TECHNICAL_CATEGORIES.has(_.get(gqlError, 'extensions.category')) ||
        // graphql-php debug mode leaking internals
        gqlError?.debugMessage !== undefined ||
        gqlError?.trace !== undefined ||
        gqlError?.file !== undefined
    );
};

/**
 * Resolves any (Apollo) error to user-presentable copy:
 * 1. known structural `extensions.code` → localized copy (all environments)
 * 2. raw backend message, except on production where recognizably technical
 *    errors (GraphQL validation/coercion dumps, internal errors) are replaced
 *    with a generic message. Sentry still receives the raw error via the
 *    Apollo error link.
 */
export const resolveErrorMessage = (err: unknown, t: TFunction): string => {
    if (err == null) {
        return t(GENERIC_ERROR_MESSAGE);
    }

    const code = collectGraphQLErrors(err)
        .map((gqlError) => _.get(gqlError, 'extensions.code'))
        .find((c) => c && STRUCTURAL_ERROR_MESSAGES[c]);
    if (code) {
        return t(STRUCTURAL_ERROR_MESSAGES[code]);
    }

    const raw = tryGetErrorMessage(err);
    if (!raw || !raw.trim()) {
        return t(GENERIC_ERROR_MESSAGE);
    }

    if (getEnvironment() === 'production' && isTechnicalError(err, raw)) {
        return t(GENERIC_ERROR_MESSAGE);
    }

    return raw;
};

/**
 * For the shared lib's <Error errorObject={...}/> panel: returns the error
 * unchanged when its message may be shown as-is, otherwise a synthetic
 * stand-in carrying only the resolved copy (which also empties the panel's
 * hidden double-click debug view).
 */
export const sanitizeErrorForDisplay = (err: any, t: TFunction): any => {
    const resolved = resolveErrorMessage(err, t);
    if (resolved === tryGetErrorMessage(err)) {
        return err;
    }
    return {message: resolved, graphQLErrors: [{message: resolved}]};
};
